import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, firstValueFrom, from, timeout } from 'rxjs';
import { AuthService } from '../../features/auth/providers/auth.service';
import { AuthActions, authFeature } from '../../features/auth/state';
import { readStoredSession, writeTokenPair } from '../utils/auth-session-storage';
import { ToastService } from './toast.service';

// `adopted` tells whether the pair came from storage (another request or tab renewed it) rather
// than from a refresh call made for this renewal.
export type RenewalOutcome =
  | { status: 'renewed'; token: string; adopted: boolean }
  | { status: 'session-ended' }
  | { status: 'transient' };

export interface RenewOptions {
  // Background renewals do not toast a connection problem nobody asked about.
  silent?: boolean;
}

const REFRESH_LOCK_NAME = 'pise-auth-refresh';
// Upper bound for the refresh call so the shared promise and the cross-tab lock always settle.
export const REFRESH_TIMEOUT_MS = 30_000;
// Minimum gap between two proactive renewals, whatever the new token's expiry looks like to the
// local clock (a skewed clock or a very short lifetime must not cause a refresh storm).
export const MIN_RENEWAL_INTERVAL_MS = 30_000;
// Minimum gap between two connection toasts, so an outage does not stack them.
export const CONNECTION_TOAST_THROTTLE_MS = 10_000;

// Owns the one renewal that may be running at a time. Concurrent callers share it; across tabs
// the Web Locks API serialises renewals and the stored pair is re-read once the lock is held, so
// a tab that waited adopts what another tab already rotated instead of replaying a used token.
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  // ToastService and TranslateService are resolved lazily: TranslateService depends on
  // HttpClient, and this service is created from inside the HTTP interceptor chain.
  private readonly injector = inject(Injector);
  private readonly token = this.store.selectSignal(authFeature.selectToken);
  private readonly refreshToken = this.store.selectSignal(authFeature.selectRefreshToken);
  private inFlight: Promise<RenewalOutcome> | null = null;
  // A renewal started for the background timer is quiet; it turns noisy if an interactive
  // caller joins it.
  private silentRenewal = false;
  private lastConnectionToastAt: number | null = null;
  private lastRenewedAt: number | null = null;

  // `failedToken` is the token the caller's request was rejected with. A pair found in storage is
  // only adopted when it differs from it. Callers that join a running renewal share its result.
  renew(failedToken: string | null = null, options: RenewOptions = {}): Observable<RenewalOutcome> {
    if (!this.inFlight) {
      this.silentRenewal = options.silent === true;
      this.inFlight = this.runRenewal(failedToken).finally(() => (this.inFlight = null));
    } else if (!options.silent) {
      this.silentRenewal = false;
    }

    return from(this.inFlight);
  }

  // Milliseconds until another proactive renewal is allowed; 0 when it is allowed now. Only a
  // real refresh call counts, so a new token that looks expired to the local clock cannot make
  // callers renew back to back. The 401 flow ignores this and stays the safety net.
  msUntilRenewalAllowed(): number {
    if (this.lastRenewedAt === null) {
      return 0;
    }

    return Math.max(0, this.lastRenewedAt + MIN_RENEWAL_INTERVAL_MS - Date.now());
  }

  // Safe to call from several failing requests: once the state is reset there is no token left
  // and nothing more is dispatched.
  endSession(): void {
    if (!this.token()) {
      return;
    }

    this.store.dispatch(AuthActions.sessionExpired({ returnUrl: this.currentUrl() }));
  }

  // The URL the user is on or heading to, so signing in again can bring them back. During startup
  // or a pending navigation `router.url` is still the previous one.
  private currentUrl(): string {
    const navigation = this.router.currentNavigation();

    if (navigation) {
      return this.router.serializeUrl(navigation.extractedUrl);
    }

    return (this.router.navigated ? this.router.url : this.location.path(true)) || '/';
  }

  private async runRenewal(failedToken: string | null): Promise<RenewalOutcome> {
    const sentRefreshToken = this.refreshToken();
    let outcome: RenewalOutcome;

    try {
      outcome = await this.withLock(() => this.renewInLock(sentRefreshToken, failedToken));
    } catch {
      outcome = { status: 'transient' };
    }

    // Side effects must never turn a computed outcome into a rejection of the shared promise.
    try {
      if (outcome.status === 'session-ended') {
        this.endSession();
      } else if (outcome.status === 'transient' && !this.silentRenewal) {
        this.notifyConnectionProblem();
      }
    } catch {
      // The outcome is already decided; a failing toast or navigation must not hide it.
    }

    return outcome;
  }

  private notifyConnectionProblem(): void {
    const now = Date.now();

    if (
      this.lastConnectionToastAt !== null &&
      now - this.lastConnectionToastAt < CONNECTION_TOAST_THROTTLE_MS
    ) {
      return;
    }

    this.lastConnectionToastAt = now;
    const message = this.injector.get(TranslateService).instant('errors.connection');
    this.injector.get(ToastService).error(message);
  }

  private withLock<T>(task: () => Promise<T>): Promise<T> {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;

    return locks ? (locks.request(REFRESH_LOCK_NAME, task) as Promise<T>) : task();
  }

  private async renewInLock(
    sentRefreshToken: string | null,
    failedToken: string | null,
  ): Promise<RenewalOutcome> {
    const stored = readStoredSession();

    if (!stored.token || !stored.refreshToken) {
      return { status: 'session-ended' };
    }

    // Another tab renewed while this one waited for the lock: adopt its pair, unless it is the
    // very token that just failed, which would only repeat the rejection.
    if (stored.refreshToken !== sentRefreshToken && stored.token !== failedToken) {
      return this.adopt(stored.token, stored.refreshToken);
    }

    let response: Awaited<ReturnType<typeof this.requestRenewal>>;

    try {
      response = await this.requestRenewal(stored.refreshToken);
    } catch (error) {
      return error instanceof HttpErrorResponse && error.status === 401
        ? { status: 'session-ended' }
        : { status: 'transient' };
    }

    // The session may have been closed or replaced while the request was in flight.
    const current = readStoredSession();

    if (!this.token() || !current.token || !current.refreshToken) {
      return { status: 'session-ended' };
    }

    if (current.refreshToken !== stored.refreshToken) {
      return this.adopt(current.token, current.refreshToken);
    }

    const token = usableString(response?.token);

    if (!token) {
      return { status: 'transient' };
    }

    // A response without a new refresh token keeps the current one rather than dropping it.
    const refreshToken = usableString(response?.refreshToken) ?? stored.refreshToken;

    writeTokenPair({ token, refreshToken });
    this.lastRenewedAt = Date.now();
    this.store.dispatch(AuthActions.tokensRenewed({ token, refreshToken }));

    return { status: 'renewed', token, adopted: false };
  }

  private adopt(token: string, refreshToken: string): RenewalOutcome {
    this.store.dispatch(AuthActions.tokensRenewed({ token, refreshToken }));

    return { status: 'renewed', token, adopted: true };
  }

  private requestRenewal(refreshToken: string) {
    return firstValueFrom(this.authService.refresh(refreshToken).pipe(timeout(REFRESH_TIMEOUT_MS)));
  }
}

function usableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
